import { Info } from "lucide-react";
import { useTaxYear } from "../contexts/TaxYearContext";
import { useAuth } from "../contexts/AuthContext";

/**
 * Selector synchronization banner — shows on CACFP pages when the user's CACFP
 * fiscal year span differs from the global Tax Year selector.
 *
 * Why: CACFP FY is operator-choice (calendar OR USDA Oct–Sept). The global Tax Year
 * always tracks the calendar year for Schedule C. When a user is on USDA FY mode,
 * the two values can be the same number but cover different month spans — this
 * banner explains the difference and prevents support tickets like "why don't my
 * CACFP numbers match my Schedule C?"
 *
 * Hidden when:
 *   - user is on calendar mode (no straddle ever)
 *   - props.fiscalYear is null/undefined
 *
 * Props:
 *   - fiscalYear (number): the CACFP page's local FY value
 */
export default function FySyncBanner({ fiscalYear }) {
  const { taxYear } = useTaxYear();
  const { user } = useAuth();
  const mode = user?.cacfp_fy_mode || "calendar";

  // No banner needed when calendar mode (CACFP FY === Tax Year always)
  if (mode !== "usda_fy" || !fiscalYear) return null;

  // USDA FY Y covers Oct (Y-1) – Sept Y
  const span = `Oct ${fiscalYear - 1} – Sept ${fiscalYear}`;

  return (
    <div
      className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-start gap-2 text-sm text-blue-900"
      data-testid="fy-sync-banner"
    >
      <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
      <div>
        <strong>CACFP Fiscal Year {fiscalYear}</strong> spans {span}.
        {fiscalYear !== taxYear && (
          <> Your Tax Year selector is set to <strong>{taxYear}</strong> — these are independent on purpose.</>
        )}
        {fiscalYear === taxYear && (
          <> Your Tax Year is also {taxYear} (Jan–Dec) — same label, different month span.</>
        )}
      </div>
    </div>
  );
}
